import type { GkDb, MapFileRow } from '../sync/db.ts'
import type { MapLayerRow } from '../data/geo.ts'

export class MapFileError extends Error {
  readonly code: 'network' | 'size' | 'hash' | 'http'
  constructor(code: MapFileError['code'], message: string) { super(message); this.code = code }
}

export interface DownloadOptions {
  url: string
  bytes: number
  sha256: string
  onProgress?: (done: number, total: number) => void
  signal?: AbortSignal
  fetchImpl?: typeof fetch
}

async function sha256Hex(data: Uint8Array): Promise<string> {
  const d = new Uint8Array(await crypto.subtle.digest('SHA-256', data as unknown as BufferSource))
  return Array.from(d, (b) => b.toString(16).padStart(2, '0')).join('')
}

/** Download a map archive and PROVE it is the published one: the announced size and the announced hash. Nothing that fails either is kept. */
export async function downloadMapFile(o: DownloadOptions): Promise<Blob> {
  const f = o.fetchImpl ?? fetch
  let res: Response
  try { res = await f(o.url, { signal: o.signal }) } catch (e) { throw new MapFileError('network', e instanceof Error ? e.message : String(e)) }
  if (!res.ok || !res.body) throw new MapFileError('http', `HTTP ${res.status}`)
  const reader = res.body.getReader()
  const chunks: Uint8Array[] = []
  let done = 0
  try {
    for (;;) {
      const r = await reader.read()
      if (r.done) break
      chunks.push(r.value); done += r.value.byteLength
      o.onProgress?.(done, o.bytes)
    }
  } catch (e) { throw new MapFileError('network', e instanceof Error ? e.message : String(e)) }
  if (done !== o.bytes) throw new MapFileError('size', `${done} bytes received, ${o.bytes} announced`)
  const all = new Uint8Array(done)
  let at = 0
  for (const c of chunks) { all.set(c, at); at += c.byteLength }
  if ((await sha256Hex(all)) !== o.sha256) throw new MapFileError('hash', 'the file does not match its published hash')
  return new Blob([all], { type: 'application/vnd.pmtiles' })
}

/** Keep this archive, drop any older one: a device holds one map per club. */
export async function saveMapFile(db: GkDb, layer: MapLayerRow, blob: Blob): Promise<void> {
  await db.transaction('rw', db.map_file, async () => {
    await db.map_file.put({ key: layer.sha256, layer_id: layer.id, sha256: layer.sha256, bytes: layer.bytes, blob, saved_at: new Date().toISOString() })
    await db.map_file.where('key').notEqual(layer.sha256).delete()
  })
}
export const getMapFile = (db: GkDb, layer: Pick<MapLayerRow, 'sha256'>): Promise<MapFileRow | undefined> => db.map_file.get(layer.sha256)

/** The newest published layer of a kind. */
export function newestLayer(layers: MapLayerRow[], kind: MapLayerRow['kind'] = 'ortho_pmtiles'): MapLayerRow | undefined {
  return layers.filter((l) => l.kind === kind).sort((a, b) => b.version - a.version)[0]
}
